/**
 * Pure resolvers that map a validation error to a table cell address
 * (row index + column index), independent of whether the node number is
 * present (PSTUDIO-3991).
 *
 * A content-type error (e.g. invalid button content) carries a stable
 * `nodeId` in the compiler diagnostic. Resolving by id first makes the cell
 * highlight AND the click-to-navigate work even when the node number is blank
 * — the case shared with PSTUDIO-3990.
 *
 * These are the addressing primitives for EVERY error source that flows
 * through `buildWorkflowErrors` (bot-compiler API + agent-validator PIF), so
 * a single fix covers all of them.
 *
 * NOTE the `-1` handling: a compiler index of `-1` means "not found" and must
 * never be treated as a real position, while an index of `0` (the first row /
 * column) is perfectly valid. The old `a || b || c` chain conflated both — it
 * swallowed `0` and let a truthy `-1` win — which is exactly why content errors
 * lost their address.
 */

type RowLike = { id?: string | null; nodeNumber?: number | string | null };

/** A usable position: a real, non-negative index (0 included, -1/undefined excluded). */
const isUsableIndex = (i: number | null | undefined): i is number =>
  typeof i === 'number' && i >= 0;

const isPresent = (v: unknown): boolean =>
  v !== null && v !== undefined && v !== '';

export type ErrorRowAddress = {
  /** Stable node id from the diagnostic (`data.id`) — the most reliable address. */
  nodeId?: string | null;
  /** The compiler's node number (`node_num`); may be a string or absent. */
  nodeNumber?: number | string | null;
  /** A sorted row index the compiler already resolved (`node_index`). */
  nodeIndex?: number | null;
  /** A row index derived from `row_num` (`row_num - 2`). */
  rowIndexFromRowNum?: number | null;
};

export type ErrorColAddress = {
  /** The compiler's column index (`cell_index`). */
  cellIndex?: number | null;
  /** The resolved column id (`column` ?? mapped `field_name`). */
  colId?: string | null;
};

/**
 * Resolve the sorted row index for an error against the current (sorted) rows.
 * Priority: nodeId → nodeIndex → node-number lookup → row_num-derived index.
 * Returns `-1` when no address resolves (caller maps `-1` to `null`).
 */
export const resolveErrorRowIndex = (
  addr: ErrorRowAddress,
  rows: RowLike[]
): number => {
  // 1. nodeId — survives a blank node number (PSTUDIO-3991/3990).
  if (isPresent(addr.nodeId)) {
    const byId = rows.findIndex((r) => r.id === addr.nodeId);
    if (byId !== -1) return byId;
  }

  // 2. A sorted index the compiler already resolved (0 is valid).
  if (isUsableIndex(addr.nodeIndex)) return addr.nodeIndex;

  // 3. Node-number lookup — loose match (compiler sends a string, the row may
  //    hold a number). A blank row number never matches a blank error number.
  if (isPresent(addr.nodeNumber)) {
    const target = String(addr.nodeNumber);
    const byNum = rows.findIndex(
      (r) => isPresent(r.nodeNumber) && String(r.nodeNumber) === target
    );
    if (byNum !== -1) return byNum;
  }

  // 4. row_num-derived index (0 is valid) — LAST on purpose. `row_num` counts
  //    rows in the compiler payload, which is built from `excludeGroupNodes(rows)`
  //    (table-store.ts validate() / PIF prep), whereas this resolver runs against
  //    the full `rows` (group headers included). In a grouped table that index is
  //    shifted up by the number of group headers above it and would point at the
  //    wrong row, so it is only a fallback once the id / node-index / node-number
  //    strategies — which all address the real `rows` — have missed. Keeping the
  //    node-number lookup (step 3) ahead of it is therefore deliberate, not a
  //    precedence bug.
  if (isUsableIndex(addr.rowIndexFromRowNum)) return addr.rowIndexFromRowNum;

  return -1;
};

/**
 * Resolve the column index for an error.
 * Priority: cellIndex → getColumnIndex(colId). Returns `-1` when unresolved.
 */
export const resolveErrorColIndex = (
  addr: ErrorColAddress,
  getColumnIndex: (colId: string) => number
): number => {
  // 1. A column index the compiler already resolved (0 is valid).
  if (isUsableIndex(addr.cellIndex)) return addr.cellIndex;

  // 2. Resolve from the column id.
  if (isPresent(addr.colId)) {
    const idx = getColumnIndex(addr.colId as string);
    if (isUsableIndex(idx)) return idx;
  }

  return -1;
};
